import { useEffect, useReducer, useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import * as studentAssignmentService from "@/api/studentAssignmentService";
import * as importService from "@/api/importService";
import { userService } from "@/api/userService";
import { exportStudentReport } from "@/api/dashboardService";
import { parseApiError } from "@/utils/errorHandler";
import { saveAs } from "file-saver";
import {
    assignmentsReducer,
    createInitialState,
} from "./studentAssignmentsReducer";
import type { simulation } from "../dashboard/types.api";
import type { paginatedResponse } from "@/types/api/paginated.api";

const PAGE_SIZE = 10;

export const useStudentAssignments = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [state, dispatch] = useReducer(
        assignmentsReducer,
        createInitialState(),
    );

    const [assignedPage, setAssignedPage] = useState(1);
    const [availablePage, setAvailablePage] = useState(1);
    const [availableFilters, setAvailableFilters] = useState({ search: "" });
    const debouncedSearch = useDebounce(availableFilters.search, 500);

    const fetchStudentData = useCallback(async () => {
        if (!userId) return null;
        return userService.getById(userId);
    }, [userId]);

    const fetchAssignments = useCallback(async () => {
        if (!userId) return;
        dispatch({ type: "FETCH_START" });
        try {
            const [assignedSimulationsArray, availableData] = await Promise.all([
                studentAssignmentService.getAssignedSimulations(userId),
                studentAssignmentService.getAvailableSimulations(userId, {
                    page: availablePage,
                    page_size: PAGE_SIZE,
                    search: debouncedSearch,
                }),
            ]);

            const assignedDataPaginated: paginatedResponse<simulation> = {
                count: assignedSimulationsArray.length,
                next: null,
                previous: null,
                results: assignedSimulationsArray,
            };

            dispatch({ type: "SET_ASSIGNED", payload: assignedDataPaginated });
            dispatch({ type: "SET_AVAILABLE", payload: availableData });
        } catch (err) {
            dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
        }
    }, [userId, availablePage, debouncedSearch]);

    const refetchData = useCallback(async () => {
        if (!state.student) {
            const studentData = await fetchStudentData();
            if (studentData) {
                dispatch({ type: "SET_STUDENT", payload: studentData });
            }
        }
        fetchAssignments();
    }, [fetchStudentData, fetchAssignments, state.student]);

    useEffect(() => {
        if (!state.student) {
            fetchStudentData().then((data) => {
                if (data) {
                    dispatch({ type: "SET_STUDENT", payload: data });
                }
            });
        }
    }, [fetchStudentData, state.student]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    const performMutation = useCallback(
        async (
            mutationFn: (userId: string, simId: number | string) => Promise<void>,
            simId: number | string,
        ) => {
            if (!userId) return;
            dispatch({ type: "SUBMIT_START" });
            try {
                await mutationFn(userId, simId);
                await refetchData();
                dispatch({ type: "SUBMIT_SUCCESS" });
            } catch (err) {
                dispatch({ type: "SUBMIT_FAILURE", payload: parseApiError(err) });
            }
        },
        [userId, refetchData],
    );

    const handleAssign = useCallback(
        (sim: simulation) =>
            performMutation(studentAssignmentService.assignSimulation, sim.id),
        [performMutation],
    );
    const handleUnassign = useCallback(
        (sim: simulation) => {
            if (window.confirm(`¿Quitar la asignación de "${sim.name}"?`)) {
                performMutation(studentAssignmentService.unassignSimulation, sim.id);
            }
        },
        [performMutation],
    );

    const handleReset = useCallback(
        (sim: simulation) => {
            if (window.confirm(`¿Seguro que quieres reiniciar el intento de "${sim.name}" para este estudiante? Se borrarán todas sus respuestas y su progreso.`)) {
                performMutation(studentAssignmentService.resetSimulationAttempt, sim.id);
            }
        },
        [performMutation],
    );

    const handleTabChange = useCallback(
        (tab: "assigned" | "available") =>
            dispatch({ type: "SET_ACTIVE_TAB", payload: tab }),
        [],
    );
    const handleExport = useCallback(
        async (sim: simulation) => {
            if (!userId || !state.student) return;
            dispatch({ type: "EXPORT_START", payload: sim.id });
            try {
                const blob = await exportStudentReport(userId, sim.id);
                const fileName = `reporte_${state.student.identifier}_${sim.name.replace(/\s+/g, "_")}.xlsx`;
                saveAs(blob, fileName);
                dispatch({ type: "EXPORT_SUCCESS" });
            } catch (err) {
                dispatch({ type: "EXPORT_FAILURE", payload: parseApiError(err) });
            }
        },
        [userId, state.student],
    );

    const handleFileUpload = useCallback(
        async (sim: simulation, file: File) => {
            if (!userId) return;
            dispatch({ type: "UPLOAD_START", payload: sim.id });
            try {
                await importService.uploadStudentAnswers(userId, sim.id, file);
                dispatch({ type: "UPLOAD_SUCCESS" });
                alert("Archivo subido. Las respuestas se están procesando.");
                await refetchData();
            } catch (err) {
                dispatch({ type: "UPLOAD_FAILURE", payload: parseApiError(err) });
            }
        },
        [userId, refetchData],
    );

    const handleConfigureAnswers = (sim: simulation) =>
        navigate(
            `/facilitator-panel/user-management/${userId}/assignments/${sim.id}/answers`,
        );

    const handleViewDashboard = (sim: simulation) =>
        navigate(`/student-dashboard?simulationId=${sim.id}&studentId=${userId}`, {
            state: { from: location.pathname + location.search },
        });

    return {
        state,
        assignedPage,
        setAssignedPage,
        availablePage,
        setAvailablePage,
        availableFilters,
        setAvailableFilters,
        handleAssign,
        handleUnassign,
        handleReset,
        handleTabChange,
        handleExport,
        handleViewDashboard,
        handleConfigureAnswers,
        handleFileUpload,
        userId,
    };
};
